import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import {
  BellRing,
  Building2,
  CalendarDays,
  Info,
  User,
  UserRound,
  UserX,
} from "lucide-react";



import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";

import { displayFormatDate, formatToDateOnly } from "../../../utils/helpers.js";
import { showError, showSuccess } from "../../utils/toast.ts";
import { ApiError, getApi, postApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import {
  API_OFFBOARDING_PAYROLL_MAIL,
  API_GET_EMPLOYEE_FILE,
  offboardingTerminationUrl,
} from "../../../config/Endpoints.js";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";

import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";

import { Card } from "../../components/shared/card/card.tsx";

import {

  CooActionModal,

} from "../../components/features/offboarding/modals/index.ts";

import { useOffboardingList } from "../../components/features/offboarding/hooks/index.ts";

import type {

  OffboardingListItem,

  OffboardingStatus,

} from "../../components/features/offboarding/schemas/index.ts";

import {
  isDepartmentCompleted,
  useOffboardingRole,
} from "../../components/features/offboarding/utils/offboarding-roles.ts";
import {
  formatOffboardingStatusLabel,
  OFFBOARDING_LIST_STATUS_FILTER_OPTIONS,
} from "../../components/features/offboarding/utils/offboarding-status-display.ts";

import {
  ResponsiveDataTable,
  Avatar,
  Badge,
  Spinner,
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  Modal,
  Select,
  TextArea,
  TextField,
  type SelectOption,
  type ResponsiveColumn,
  type BadgeVariant,
} from "../../components/ui/index.ts";
import { useEmployeesList } from "../../hooks/time-allocated-overview/use-employees-list.ts";

import type { PageProps } from "../../types/page.ts";

import styles from "./offboard-l1-page.module.css";

export type OffboardingReviewRole =
  | "EXECUTIVE"
  | "IT"
  | "ADMIN"
  | "PAYROLL"
  | "HR"
  | "RM";

const ROLE_ROUTE: Record<OffboardingReviewRole, string> = {
  EXECUTIVE: "/dashboard/offboardl1",
  IT: "/dashboard/offboardlIT",
  ADMIN: "/dashboard/offboardladmin",
  PAYROLL: "/dashboard/offboardlpayroll",
  HR: "/dashboard/offboardlhr",
  RM: "/dashboard/offboardlrm",
};

const ROLE_DEPARTMENT: Partial<
  Record<OffboardingReviewRole, "IT" | "ADMIN" | "PAYROLL" | "HR" | "RM">
> = {
  IT: "IT",
  ADMIN: "ADMIN",
  PAYROLL: "PAYROLL",
  HR: "HR",
  RM: "RM",
};

const CLEARANCE_REVIEW_ROLES = new Set<OffboardingReviewRole>([
  "IT",
  "ADMIN",
  "PAYROLL",
  "RM",
]);

type OffboardL1PageProps = PageProps & {
  reviewRole?: OffboardingReviewRole;
};

const STATUS_FILTER_OPTIONS = OFFBOARDING_LIST_STATUS_FILTER_OPTIONS;

const CLEARANCE_REVIEW_STATUSES = new Set<OffboardingStatus>([
  "LWD_CONFIRMED",
  "CLEARANCE_IN_PROGRESS",
]);
const EXCLUDED_FROM_ALL_LIST_STATUSES = new Set<OffboardingStatus>([
  "RETENTION_REQUESTED",
  "RESIGNATION_PENDING",
]);
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CLEARANCE_ACTION_DAYS_BEFORE_LWD = 7;
const CLEARANCE_ACTION_UNAVAILABLE_MESSAGE =
  "Clearance actions will be available 7 days before the last working day.";
type EmailTemplateKey = "TERMINATION" | "PIP";
type TemplateMode = "preview" | "edit";
type EmailTemplateContent = {
  emailTitle: string;
  emailSubtitle: string;
  subject: string;
  emailBody: string;
};

const DEFAULT_TERMINATION_EMAIL_BODY = `This is to formally notify you that, following a comprehensive review by the management, a decision has been made to discontinue your employment with Caelius Consulting.

This decision has been taken after careful consideration of business, operational, and organizational requirements and has been approved by the appropriate stakeholders.

As part of this process, you are required to complete all applicable transition activities, pending assignments, knowledge transfer requirements, and company clearance formalities before & on your Last Working Day.

You may review the details of your offboarding process, pending actions, and timelines through the portal.

We appreciate the contributions made during your association with the organization and thank you for your efforts and commitment. We wish you success and fulfillment in your future professional endeavors.

`;

const DEFAULT_PIP_EMAIL_BODY = `This is to formally inform you that certain performance-related concerns have been identified that require immediate attention and improvement.

To support your development and provide a structured opportunity for improvement, you are being placed on a Performance Improvement Plan (PIP).

During this period, your performance will be regularly reviewed against defined objectives and expectations. 

You are expected to demonstrate improvement in accountability, task execution, adherence to timelines, communication effectiveness, and overall consistency in performance.

Your Reporting Manager will provide guidance, feedback, and support throughout the PIP period.

Please note that successful completion of the PIP requires satisfactory and sustained improvement in performance. Failure to achieve the expected improvement may result in further corrective action, up to and including separation from the organization, in accordance with company policies.

For detailed objectives, review milestones, and updates, please access the PIP details through the portal.`;

const DEFAULT_EMAIL_TEMPLATES: Record<EmailTemplateKey, EmailTemplateContent> = {
  TERMINATION: {
    emailTitle: "Employment Separation Notification",
    emailSubtitle: "Official separation communication",
    subject: "Employment Separation Notification",
    emailBody: DEFAULT_TERMINATION_EMAIL_BODY,
  },
  PIP: {
    emailTitle: "Performance Improvement Plan",
    emailSubtitle: "Performance improvement plan communication",
    subject: "Performance Improvement Plan Notification",
    emailBody: DEFAULT_PIP_EMAIL_BODY,
  },
};

const EMAIL_TEMPLATE_OPTIONS: SelectOption[] = [
  { value: "TERMINATION", label: "Employment Separation" },
  { value: "PIP", label: "PIP" },
];

function createDefaultEmailTemplates(): Record<EmailTemplateKey, EmailTemplateContent> {
  return {
    TERMINATION: { ...DEFAULT_EMAIL_TEMPLATES.TERMINATION },
    PIP: { ...DEFAULT_EMAIL_TEMPLATES.PIP },
  };
}

type OffboardL1TableRow = OffboardingListItem & { id: string } & Record<
  string,
  unknown
>;

type TerminationDetails = {
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  designation: string;
  department: string;
  manager: string;
  expectedLwd: string;
  photoPath?: string;
};

function offboardingStatusBadgeVariant(
  status: string | undefined,
): BadgeVariant {
  const u = (status ?? "").toUpperCase();

  if (u === "REJECTED" || u === "WITHDRAWN") return "danger";

  if (u === "RESIGNATION_SUBMITTED" || u === "UNDER_REVIEW") return "warning";
  if (u === "LWD_CONFIRMED" || u === "CLEARANCE_IN_PROGRESS") return "success";
  if (u === "EXIT_FORM_PENDING") return "warning";
  if (u === "HR_CLEARANCE_PENDING") return "warning";
  if (u === "EXIT_COMPLETED") return "success";

  return "neutral";
}

function isClearanceReviewStatus(status: string): boolean {
  return status === "LWD_CONFIRMED" || status === "CLEARANCE_IN_PROGRESS";
}

function shouldShowViewDetails(status: string): boolean {
  return status !== "WITHDRAWN" && status !== "REJECTED";
}

function formatFinalLwd(finalLwd: string | null | undefined): string {
  if (finalLwd == null || finalLwd.trim() === "") {
    return "—";
  }

  const formatted = displayFormatDate(finalLwd);
  return formatted === "-" ? "—" : formatted;
}

function parseUtcDateMs(value: string | null | undefined): number | null {
  if (value == null || value.trim() === "") return null;

  const [datePart] = value.split("T");
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? null
      : Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
      );
  }

  const [year, month, day] = parts;
  return Date.UTC(year, month - 1, day);
}

function startOfTodayUtcMs(): number {
  const today = new Date();
  return Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
}

function daysUntilUtcDate(dateMs: number): number {
  return Math.ceil((dateMs - startOfTodayUtcMs()) / MS_PER_DAY);
}

function canTakeClearanceAction(finalLwd: string | null | undefined): boolean {
  const finalLwdDateMs = parseUtcDateMs(finalLwd);
  if (finalLwdDateMs == null) return false;

  return daysUntilUtcDate(finalLwdDateMs) <= CLEARANCE_ACTION_DAYS_BEFORE_LWD;
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (value == null || value.trim() === "") return null;
  const [datePart] = value.split("T");
  const parts = datePart.split("-").map(Number);
  if (parts.length === 3 && parts.every((part) => Number.isFinite(part))) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatEmailLwd(finalLwd: Date | null, fallback: string): string {
  if (finalLwd == null) return fallback;

  return formatFinalLwd(formatToDateOnly(finalLwd));
}

function formatEmailDate(value: Date | null, fallback: string): string {
  if (value == null) return fallback;

  return displayFormatDate(formatToDateOnly(value));
}

function renderEmailTemplateBody(
  body: string,
  values: {
    startDate: string;
    endDate: string;
    employeeName: string;
    employeeCode: string;
    department: string;
    designation: string;
  },
): string {
  const renderedBody = body
    .replaceAll("[[{startDate}]]", values.startDate)
    .replaceAll("[[{endDate}]]", values.endDate)
    .replaceAll("[[{finalLwd}]]", values.endDate)
    .replaceAll("[[{employeeName}]]", values.employeeName)
    .replaceAll("[[{employeeCode}]]", values.employeeCode)
    .replaceAll("[[{department}]]", values.department)
    .replaceAll("[[{designation}]]", values.designation)
    .replaceAll("[Start Date]", values.startDate)
    .replaceAll("[End Date]", values.endDate)
    .replaceAll("[Employee Name]", values.employeeName)
    .replaceAll("[Employee ID]", values.employeeCode)
    .replaceAll("[Employee Code]", values.employeeCode)
    .replaceAll("[Department]", values.department)
    .replaceAll("[Designation]", values.designation)
    .replaceAll("[Last Working Day]", values.endDate);

  return renderedBody.replace(
    "Performance Improvement Plan (PIP).",
    `Performance Improvement Plan (PIP) effective from ${values.startDate} until ${values.endDate}.`,
  );
}

function isDirectImageUrl(value: string): boolean {
  return /^(https?:)?\/\//i.test(value) || value.startsWith("/");
}

function parseTerminationDetails(raw: unknown): TerminationDetails | null {
  const root =
    raw != null && typeof raw === "object" && "data" in raw
      ? (raw as Record<string, unknown>).data
      : raw;
  if (root == null || typeof root !== "object") return null;
  const r = root as Record<string, unknown>;
  const employeeId = Number(r.employeeId);
  if (!Number.isFinite(employeeId)) return null;

  return {
    employeeId,
    employeeCode: String(r.employeeCode ?? "").trim(),
    employeeName: String(r.employeeName ?? "").trim(),
    designation: String(r.designation ?? "").trim(),
    department: String(r.department ?? "").trim(),
    manager: String(r.manager ?? "").trim(),
    expectedLwd: String(r.expectedLwd ?? "").trim(),
    photoPath:
      typeof r.photoPath === "string"
        ? r.photoPath.trim()
        : typeof r.empPhoto === "string"
          ? r.empPhoto.trim()
        : typeof r.image === "string"
          ? r.image.trim()
          : typeof r.profileImage === "string"
            ? r.profileImage.trim()
            : undefined,
  };
}

type TerminationModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function TerminationModal({ open, onClose, onSuccess }: TerminationModalProps) {
  const {
    options: employeeOptions,
    loading: employeeOptionsLoading,
  } = useEmployeesList();
  const [employeeId, setEmployeeId] = useState("");
  const [details, setDetails] = useState<TerminationDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTemplate, setActiveTemplate] =
    useState<EmailTemplateKey>("TERMINATION");
  const [templateMode, setTemplateMode] = useState<TemplateMode>("preview");
  const [emailTemplates, setEmailTemplates] =
    useState<Record<EmailTemplateKey, EmailTemplateContent>>(
      createDefaultEmailTemplates,
    );
  const [finalLwd, setFinalLwd] = useState<Date | null>(null);
  const [pipStartDate, setPipStartDate] = useState<Date | null>(null);
  const [pipEndDate, setPipEndDate] = useState<Date | null>(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photoSrc, setPhotoSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      setEmployeeId("");
      setDetails(null);
      setDetailsLoading(false);
      setActiveTemplate("TERMINATION");
      setTemplateMode("preview");
      setEmailTemplates(createDefaultEmailTemplates());
      setFinalLwd(null);
      setPipStartDate(null);
      setPipEndDate(null);
      setRemarks("");
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || employeeId.trim() === "") {
      setDetails(null);
      setFinalLwd(null);
      setPipStartDate(null);
      setPipEndDate(null);
      return;
    }

    const ac = new AbortController();
    setDetailsLoading(true);
    setDetails(null);
    setFinalLwd(null);
    setPipStartDate(null);
    setPipEndDate(null);

    void (async () => {
      try {
        const { data } = await getApi<unknown>(
          offboardingTerminationUrl(employeeId),
          accessToken(),
          "json",
          ac.signal,
        );
        if (ac.signal.aborted) return;
        const parsed = parseTerminationDetails(data);
        if (parsed == null) {
          showError("Unable to read employee termination details.");
          return;
        }
        setDetails(parsed);
        setFinalLwd(parseDateOnly(parsed.expectedLwd));
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        showError(
          e instanceof ApiError
            ? e.message
            : "Unable to load employee termination details.",
        );
      } finally {
        if (!ac.signal.aborted) setDetailsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [employeeId, open]);

  useEffect(() => {
    const photoPath = details?.photoPath;

    if (!photoPath) {
      setPhotoSrc(undefined);
      return;
    }

    if (isDirectImageUrl(photoPath)) {
      setPhotoSrc(photoPath);
      return;
    }

    if (!photoPath.includes("photoDir")) {
      setPhotoSrc(undefined);
      return;
    }

    const ac = new AbortController();
    let objectUrl: string | undefined;
    setPhotoSrc((current) => {
      if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
      return undefined;
    });

    void getApi<Blob>(
      `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(photoPath)}`,
      accessToken(),
      "blob",
      ac.signal,
    )
      .then(({ data }) => {
        if (ac.signal.aborted) return;
        objectUrl = URL.createObjectURL(data);
        setPhotoSrc((current) => {
          if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
          return objectUrl;
        });
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setPhotoSrc((current) => {
          if (current?.startsWith("blob:")) URL.revokeObjectURL(current);
          return undefined;
        });
      });

    return () => {
      ac.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [details?.photoPath]);

  useEffect(() => {
    if (pipStartDate == null || pipEndDate == null) return;
    if (pipEndDate < pipStartDate) setPipEndDate(null);
  }, [pipStartDate, pipEndDate]);

  const closeIfIdle = useCallback(() => {
    if (!submitting) onClose();
  }, [onClose, submitting]);

  const selectedEmailTemplate = emailTemplates[activeTemplate];
  const selectedTemplateIsEdited =
    selectedEmailTemplate.subject !==
    DEFAULT_EMAIL_TEMPLATES[activeTemplate].subject ||
    selectedEmailTemplate.emailTitle !==
    DEFAULT_EMAIL_TEMPLATES[activeTemplate].emailTitle ||
    selectedEmailTemplate.emailSubtitle !==
    DEFAULT_EMAIL_TEMPLATES[activeTemplate].emailSubtitle ||
    selectedEmailTemplate.emailBody !==
    DEFAULT_EMAIL_TEMPLATES[activeTemplate].emailBody;

  const updateSelectedEmailTemplate = useCallback(
    (patch: Partial<EmailTemplateContent>) => {
      setEmailTemplates((current) => ({
        ...current,
        [activeTemplate]: {
          ...current[activeTemplate],
          ...patch,
        },
      }));
    },
    [activeTemplate],
  );

  const emailPreviewEmployeeName =
    details?.employeeName?.trim() || `[[${"{employeeName}"}]]`;
  const emailSubmitEmployeeName = details?.employeeName?.trim() || "-";
  const emailPreviewFinalLwd = formatEmailLwd(
    details == null ? null : finalLwd,
    `[[${"{finalLwd}"}]]`,
  );
  const emailSubmitFinalLwd = formatEmailLwd(
    details == null ? null : finalLwd,
    "-",
  );
  const emailPreviewPipStartDate = formatEmailDate(
    details == null ? null : pipStartDate,
    `[[${"{pipStartDate}"}]]`,
  );
  const emailPreviewPipEndDate = formatEmailDate(
    details == null ? null : pipEndDate,
    `[[${"{pipEndDate}"}]]`,
  );
  const emailSubmitPipStartDate = formatEmailDate(
    details == null ? null : pipStartDate,
    "-",
  );
  const emailSubmitPipEndDate = formatEmailDate(
    details == null ? null : pipEndDate,
    "-",
  );
  const todayDate = displayFormatDate(formatToDateOnly(new Date()));
  const separationDetails = {
    employeeName: emailPreviewEmployeeName,
    employeeCode: details?.employeeCode?.trim() || `[[${"{employeeCode}"}]]`,
    department: details?.department?.trim() || `[[${"{department}"}]]`,
    designation: details?.designation?.trim() || `[[${"{designation}"}]]`,
    lastWorkingDay: emailPreviewFinalLwd,
  };
  const separationDetailsForSubmit = {
    employeeName: emailSubmitEmployeeName,
    employeeCode: details?.employeeCode?.trim() || "-",
    department: details?.department?.trim() || "-",
    designation: details?.designation?.trim() || "-",
    lastWorkingDay: emailSubmitFinalLwd,
  };
  const separationDetailsBody = `Details:
Employee Name: ${separationDetails.employeeName}
Employee Code: ${separationDetails.employeeCode}
Department: ${separationDetails.department}
Designation: ${separationDetails.designation}
Last Working Day: ${separationDetails.lastWorkingDay}`;
  const separationDetailsBodyForSubmit = `Details:
Employee Name: ${separationDetailsForSubmit.employeeName}
Employee Code: ${separationDetailsForSubmit.employeeCode}
Department: ${separationDetailsForSubmit.department}
Designation: ${separationDetailsForSubmit.designation}
Last Working Day: ${separationDetailsForSubmit.lastWorkingDay}`;
  const renderedEmailBody = renderEmailTemplateBody(
    selectedEmailTemplate.emailBody,
    {
      startDate:
        activeTemplate === "PIP" ? emailPreviewPipStartDate : todayDate,
      endDate:
        activeTemplate === "PIP" ? emailPreviewPipEndDate : emailPreviewFinalLwd,
      employeeName: emailPreviewEmployeeName,
      employeeCode: separationDetails.employeeCode,
      department: separationDetails.department,
      designation: separationDetails.designation,
    },
  );
  const renderedEmailBodyForSubmit = renderEmailTemplateBody(
    selectedEmailTemplate.emailBody,
    {
      startDate:
        activeTemplate === "PIP" ? emailSubmitPipStartDate : todayDate,
      endDate:
        activeTemplate === "PIP" ? emailSubmitPipEndDate : emailSubmitFinalLwd,
      employeeName: emailSubmitEmployeeName,
      employeeCode: separationDetailsForSubmit.employeeCode,
      department: separationDetailsForSubmit.department,
      designation: separationDetailsForSubmit.designation,
    },
  );
  const renderedEmailBodyWithGeneratedDetails =
    activeTemplate === "TERMINATION"
      ? renderedEmailBody.replace(
        "As part of this process,",
        `${separationDetailsBody}\n\nAs part of this process,`,
      )
      : renderedEmailBody;
  const renderedEmailBodyWithGeneratedDetailsForSubmit =
    activeTemplate === "TERMINATION"
      ? renderedEmailBodyForSubmit.replace(
        "As part of this process,",
        `${separationDetailsBodyForSubmit}\n\nAs part of this process,`,
      )
      : renderedEmailBodyForSubmit;
  const renderedEmailBodyWithPipPeriodForSubmit =
    activeTemplate === "PIP"
      ? `${renderedEmailBodyWithGeneratedDetailsForSubmit}\n\nPIP Period: ${emailSubmitPipStartDate} - ${emailSubmitPipEndDate}`
      : renderedEmailBodyWithGeneratedDetailsForSubmit;
  const emailBodyForSubmit =
    activeTemplate === "TERMINATION"
      ? `${renderedEmailBodyWithGeneratedDetailsForSubmit}`
      : renderedEmailBodyWithPipPeriodForSubmit;
  const resetSelectedEmailTemplate = useCallback(() => {
    setEmailTemplates((current) => ({
      ...current,
      [activeTemplate]: { ...DEFAULT_EMAIL_TEMPLATES[activeTemplate] },
    }));
  }, [activeTemplate]);
  const isPipTemplate = activeTemplate === "PIP";
  const submitDisabled =
    submitting ||
    details == null ||
    (isPipTemplate
      ? pipStartDate == null || pipEndDate == null
      : finalLwd == null);

  const handleSubmit = useCallback(async () => {
    if (details == null) {
      showError("Please select an employee.");
      return;
    }

    if (activeTemplate === "PIP" && (pipStartDate == null || pipEndDate == null)) {
      showError("Please select PIP start and end dates.");
      return;
    }

    if (activeTemplate !== "PIP" && finalLwd == null) {
      showError("Please select an employee and final LWD.");
      return;
    }

    setSubmitting(true);
    try {
      const payload =
        activeTemplate === "PIP"
          ? {
            requestType: "PIP",
            pipStartDate: formatToDateOnly(pipStartDate as Date),
            pipEndDate: formatToDateOnly(pipEndDate as Date),
            remarks: remarks.trim() || "Performance improvement initiated.",
            subject: selectedEmailTemplate.subject,
            emailTitle: selectedEmailTemplate.emailTitle,
            emailSubtitle: selectedEmailTemplate.emailSubtitle,
            emailBody: emailBodyForSubmit,
          }
          : {
              requestType: "TERMINATION",
            finalLwd: formatToDateOnly(finalLwd as Date),
            remarks: remarks.trim(),
            subject: selectedEmailTemplate.subject,
            emailBody: emailBodyForSubmit,
            emailTitle: selectedEmailTemplate.emailTitle,
            emailSubtitle: selectedEmailTemplate.emailSubtitle,
          };

      await postApi(offboardingTerminationUrl(details.employeeId), accessToken(), payload);
      showSuccess(
        activeTemplate === "PIP"
          ? "PIP submitted successfully."
          : "Termination submitted successfully.",
      );
      onSuccess();
      onClose();
    } catch (e) {
      showError(
        e instanceof ApiError
          ? e.message
          : activeTemplate === "PIP"
            ? "Unable to submit PIP."
            : "Unable to submit termination.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    details,
    activeTemplate,
    finalLwd,
    pipStartDate,
    pipEndDate,
    onClose,
    onSuccess,
    remarks,
    emailBodyForSubmit,
    selectedEmailTemplate,
  ]);

  return (
    <Modal
      open={open}
      onClose={closeIfIdle}
      title="Initiate Employee Separation"
      size="xl"
      bodyClassName={styles.terminationModalBody}
      footer={
        <div className={styles.terminationFooter}>
          <Button variant="light-grey" onClick={closeIfIdle} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitDisabled}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      }
    >
      <div className={styles.terminationTop}>
        <Select
          label="Employee Name"
          placeholder={employeeOptionsLoading ? "Loading..." : "Select Employee"}
          searchable
          value={employeeId}
          onChange={setEmployeeId}
          options={employeeOptions}
          disabled={employeeOptionsLoading || submitting}
        />
      </div>

      <div className={styles.terminationGrid}>
        <section className={styles.templatePanel}>
          <div className={styles.templateLayout}>
            <div className={styles.templateHeader}>
              <div className={styles.templateSelect}>
                <Select
                  id="offboarding-email-template"
                  label="Notification Template"
                  value={activeTemplate}
                  onChange={(value) => setActiveTemplate(value as EmailTemplateKey)}
                  options={EMAIL_TEMPLATE_OPTIONS}
                  disabled={submitting}
                  density="compact"
                />
              </div>
              <div className={styles.templateHeaderActions}>
                <Button
                  variant="muted-outline"
                  size="sm"
                  onClick={resetSelectedEmailTemplate}
                  disabled={submitting || !selectedTemplateIsEdited}
                >
                  Reset
                </Button>
                <div className={styles.templateModeToggle}>
                  <button
                    type="button"
                    className={styles.templateModeButton}
                    data-active={templateMode === "preview"}
                    onClick={() => setTemplateMode("preview")}
                    disabled={submitting}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className={styles.templateModeButton}
                    data-active={templateMode === "edit"}
                    onClick={() => setTemplateMode("edit")}
                    disabled={submitting}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.templateWorkspace}>
              {templateMode === "edit" ? (
                <div className={styles.templateEditor}>
                  <TextField
                    id={`${activeTemplate.toLowerCase()}-email-subject`}
                    label="Subject"
                    value={selectedEmailTemplate.subject}
                    disabled={submitting}
                    onChange={(event) =>
                      updateSelectedEmailTemplate({
                        subject: event.target.value,
                      })
                    }
                  />
                  <TextField
                    id={`${activeTemplate.toLowerCase()}-email-title`}
                    label="Title"
                    value={selectedEmailTemplate.emailTitle}
                    disabled={submitting}
                    onChange={(event) =>
                      updateSelectedEmailTemplate({
                        emailTitle: event.target.value,
                      })
                    }
                  />
                  <TextField
                    id={`${activeTemplate.toLowerCase()}-email-subtitle`}
                    label="Subtitle"
                    value={selectedEmailTemplate.emailSubtitle}
                    disabled={submitting}
                    onChange={(event) =>
                      updateSelectedEmailTemplate({
                        emailSubtitle: event.target.value,
                      })
                    }
                  />
                  <TextArea
                    id={`${activeTemplate.toLowerCase()}-email-body`}
                    label="Body"
                    rows={14}
                    value={selectedEmailTemplate.emailBody}
                    disabled={submitting}
                    onChange={(event) =>
                      updateSelectedEmailTemplate({
                        emailBody: event.target.value,
                      })
                    }
                    className={styles.templateTextarea}
                  />
                  {activeTemplate === "TERMINATION" ? (
                    <p className={styles.generatedTemplateNote}>
                      Separation Details are generated from the selected employee
                      and are not editable here.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className={styles.emailPreview}>
                  <div className={styles.emailPreviewCard}>
                    <div className={styles.emailPreviewHeader}>
                      <div>
                        <h3>{selectedEmailTemplate.emailTitle || "Email Title"}</h3>
                        <p>
                          {selectedEmailTemplate.emailSubtitle ||
                            "Email Subtitle"}
                        </p>
                      </div>
                      <img
                        src="/assets/caelius-logo.svg"
                        alt="Caelius Consulting"
                        className={styles.emailPreviewLogo}
                      />
                    </div>
                    <div className={styles.emailPreviewBody}>
                      <p>Dear {emailPreviewEmployeeName},</p>

                      <div className={styles.emailContent}>
                        {renderedEmailBodyWithGeneratedDetails}
                      </div>
                      {activeTemplate === "PIP" ? (
                        <p>
                          PIP Period:{" "}
                          <strong>
                            {emailPreviewPipStartDate} - {emailPreviewPipEndDate}
                          </strong>
                        </p>
                      ) : null}

                      <p>
                        Warm Regards,
                        <br />
                        Team CaeliusOne
                        <br/>
                        Caelius Consulting
                     
                      </p>
                    </div>
                    <div className={styles.emailPreviewDivider} />
                    <p className={styles.emailPreviewNote}>
                      This is an automated message. Please do not reply to this
                      email.
                    </p>
                    <div className={styles.emailPreviewFooter}>
                      <img
                        src="/assets/caelius-logo.svg"
                        alt="Caelius Consulting"
                        className={styles.emailPreviewFooterLogo}
                      />
                      <div className={styles.emailPreviewLinks}>
                        <span>LinkedIn</span>
                        <span aria-hidden>|</span>
                        <span>Website</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={styles.detailsPanel}>
          <div className={styles.detailsPanelHeader}>
            <span className={styles.detailsHeaderIcon} aria-hidden>
              <User size={19} strokeWidth={2} />
            </span>
            <h3 className="heading-xs">Employee Details</h3>
          </div>
          <div className={styles.detailsPanelBody}>
            {detailsLoading ? (
              <div className={styles.employeeLoadingState}>
                <Spinner />
              </div>
            ) : details == null ? (
              <div className={styles.employeeEmptyState}>
                <div className={styles.employeeEmptyArtwork} aria-hidden>
                  <span className={styles.employeeEmptyIcon}>
                    <User size={48} strokeWidth={1.8} />
                  </span>
                </div>
                <h3 className="heading-xs">Select an employee to view details</h3>
                <p className="body-sm">
                 Employee information and separation details will be displayed here once an employee is selected.
                </p>
              </div>
            ) : (
              <>
                <div className={styles.employeeDetailsCard}>
                  <div className={styles.employeeProfile}>
                    <Avatar
                      name={details.employeeName || "Employee"}
                      src={photoSrc}
                      size="md"
                      className={styles.employeeAvatar}
                    />
                    <div className={styles.employeeSummary}>
                      <h4 className="heading-sm">{details.employeeName || "-"}</h4>
                      <p className={`body-sm ${styles.employeeDesignation}`}>
                        {details.designation || "-"}
                      </p>
                    </div>
                  </div>

                  <div className={styles.detailsInlineGrid}>
                    <div className={styles.detailsInlineItem}>
                      <UserRound size={20} strokeWidth={1} aria-hidden />
                      <div>
                        <span className="body-sm">Employee Code</span>
                        <p className="body-sm text-muted">
                          {details.employeeCode || "-"}
                        </p>
                      </div>
                    </div>
                    <div className={styles.detailsInlineItem}>
                      <UserRound size={20} strokeWidth={1} aria-hidden />
                      <div>
                        <span className="body-sm">Reporting Manager</span>
                        <p className="body-sm text-muted">
                          {details.manager || "-"}
                        </p>
                      </div>
                    </div>
                    <div className={styles.detailsInlineItem}>
                      <Building2 size={20} strokeWidth={1} aria-hidden />
                      <div>
                        <span className="body-sm">Department</span>
                        <p className="body-sm text-muted">
                          {details.department || "-"}
                        </p>
                      </div>
                    </div>
                    <div className={styles.detailsInlineItem}>
                      <CalendarDays size={20} strokeWidth={1} aria-hidden />
                      <div>
                        <span className="body-sm">Expected LWD</span>
                        <p className="body-sm text-muted">
                          {formatFinalLwd(details.expectedLwd)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.terminationFormCard}>
                  {activeTemplate === "PIP" ? (
                    <>
                      <DatePicker
                        id="pip-start-date"
                        label="PIP Start Date"
                        selected={pipStartDate}
                        onChange={setPipStartDate}
                        placeholderText="Select PIP start date"
                        disabled={submitting}
                        customInput={
                          <DatePickerFieldTrigger
                            className={styles.offboardingDateTrigger}
                          />
                        }
                        required
                      />
                      <DatePicker
                        id="pip-end-date"
                        label="PIP End Date"
                        selected={pipEndDate}
                        onChange={setPipEndDate}
                        placeholderText="Select PIP end date"
                        disabled={submitting}
                        minDate={pipStartDate ?? undefined}
                        customInput={
                          <DatePickerFieldTrigger
                            className={styles.offboardingDateTrigger}
                          />
                        }
                        required
                      />
                    </>
                  ) : (
                    <DatePicker
                      id="termination-final-lwd"
                      label="Final LWD"
                      selected={finalLwd}
                      onChange={setFinalLwd}
                      placeholderText="Select final LWD"
                      disabled={submitting}
                      customInput={
                        <DatePickerFieldTrigger
                          className={styles.offboardingDateTrigger}
                        />
                      }
                      required
                    />
                  )}
                  <TextArea
                    id="termination-remarks"
                    label="Remarks"
                    rows={5}
                    maxLength={1000}
                    showCharacterCount
                    value={remarks}
                    disabled={submitting}
                    onChange={(event) => setRemarks(event.target.value)}
                    placeholder="Add remarks..."
                  />
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}

export default function OffboardL1Page({
  title,
  pageMeta,
  reviewRole = "EXECUTIVE",
}: OffboardL1PageProps) {
  const pageTitle = pageMeta?.title || title;

  const {
    clearanceDepartments,
    user,
  } = useOffboardingRole();

  const scopedDepartment = ROLE_DEPARTMENT[reviewRole];
  const isExecutiveView = reviewRole === "EXECUTIVE";
  const isHrListingView = reviewRole === "HR";
  const scopedClearanceDepartments = useMemo(
    () =>
      scopedDepartment != null && CLEARANCE_REVIEW_ROLES.has(reviewRole)
        ? [scopedDepartment]
        : clearanceDepartments,
    [clearanceDepartments, reviewRole, scopedDepartment],
  );
  const isScopedClearanceApprover = CLEARANCE_REVIEW_ROLES.has(reviewRole);

  const navigate = useNavigate();

  const { setBreadcrumbs } = useBreadcrumbs();

  const [page, setPage] = useState(1);

  const [pageSize] = useState(10);

  const [statusFilter, setStatusFilter] = useState(() => {
    if (isExecutiveView) return "RESIGNATION_SUBMITTED";

    if (isHrListingView) return "HR_CLEARANCE_PENDING";

    return "";
  });
  const showPayrollHoldNotificationColumn =
    isExecutiveView && statusFilter === "RESIGNATION_SUBMITTED";

  const [cooModalOpen, setCooModalOpen] = useState(false);
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);

  const [selectedOffboardingId, setSelectedOffboardingId] = useState<
    number | null
  >(null);
  const [notificationOffboardingId, setNotificationOffboardingId] = useState<
    string | null
  >(null);
  const [notificationComment, setNotificationComment] = useState("");
  const [pushingNotificationIds, setPushingNotificationIds] = useState<
    Set<string>
  >(() => new Set());

  const { items, loading, error, refetch } = useOffboardingList({
    status: (statusFilter || undefined) as OffboardingStatus | undefined,
  });

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle || "Offboarding Review" }]);

    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, pageTitle]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  const tableRows: OffboardL1TableRow[] = useMemo(
    () =>
      items.map((row) => ({
        ...row,

        id: String(row.id),
      })),

    [items],
  );

  const filteredRows = useMemo(() => {
    let rows = tableRows;

    if (!statusFilter) {
      rows = rows.filter(
        (row) => !EXCLUDED_FROM_ALL_LIST_STATUSES.has(row.status),
      );
    }

    if (reviewRole === "RM" && user?.id) {
      rows = rows.filter((row) => {
        const managerId = row.managerId;
        return managerId === user.id || managerId === String(user.id);
      });
    }

    if (isScopedClearanceApprover) {
      rows = rows.filter((row) => {
        if (!CLEARANCE_REVIEW_STATUSES.has(row.status)) return false;

        const actionableDepartments = scopedClearanceDepartments.filter(
          (department) => !isDepartmentCompleted(department, row),
        );

        if (actionableDepartments.length > 0) return true;

        return false;
      });
    }

    return rows;
  }, [tableRows, statusFilter, isScopedClearanceApprover, scopedClearanceDepartments, reviewRole, user?.id]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const openCooModal = useCallback((offboardingId: number) => {
    setSelectedOffboardingId(offboardingId);

    setCooModalOpen(true);
  }, []);

  const closeCooModal = useCallback(() => {
    setCooModalOpen(false);

    setSelectedOffboardingId(null);
  }, []);

  const handleStatusFilterChange = useCallback((status: string) => {
    setStatusFilter(status);

    setPage(1);
  }, []);

  const navigateToDetail = useCallback(
    (offboardingId: string) => {
      navigate(`${ROLE_ROUTE[reviewRole]}/${offboardingId}`, {
        state: { breadcrumbTitle: pageTitle },
      });
    },

    [navigate, reviewRole, pageTitle],
  );

  const handlePushPayrollNotification = useCallback(async () => {
    if (notificationOffboardingId == null) return;
    const offboardingId = notificationOffboardingId;

    setPushingNotificationIds((current) => {
      const next = new Set(current);
      next.add(offboardingId);
      return next;
    });

    try {
      await postApi(
        `${API_OFFBOARDING_PAYROLL_MAIL}/${offboardingId}`,
        accessToken(),
        { comment: notificationComment.trim() },
      );
      showSuccess("Payroll hold notification sent successfully.");
      setNotificationOffboardingId(null);
      setNotificationComment("");
    } catch {
      showError("Unable to send payroll hold notification.");
    } finally {
      setPushingNotificationIds((current) => {
        const next = new Set(current);
        next.delete(offboardingId);
        return next;
      });
    }
  }, [notificationComment, notificationOffboardingId]);

  const closeNotificationModal = useCallback(() => {
    if (
      notificationOffboardingId != null &&
      pushingNotificationIds.has(notificationOffboardingId)
    ) {
      return;
    }

    setNotificationOffboardingId(null);
    setNotificationComment("");
  }, [notificationOffboardingId, pushingNotificationIds]);

  const columns = useMemo<ResponsiveColumn<OffboardL1TableRow>[]>(
    () => [
      {
        id: "employeeName",

        header: "Employee Name",

        accessor: "employeeName",

        sortable: true,

        nowrap: true,

        render: (row) => <span className="body-sm">{row.employeeName}</span>,

        getSortValue: (row) => row.employeeName,
      },

      {
        id: "resignationType",

        header: "Resignation Type",

        sortable: true,

        nowrap: true,

        render: (row) => (
          <span className="body-sm">
            {row.isTermination ? "Terminated" : "Resignation"}
          </span>
        ),

        getSortValue: (row) =>
          row.isTermination ? "Terminated" : "Resignation",

        getTitleText: (row) =>
          row.isTermination ? "Terminated" : "Resignation",
      },

      ...(isExecutiveView
        ? [
          {
            id: "resignationDate",

            header: "Resignation Date",

            sortable: true,

            nowrap: true,

            render: (row: OffboardL1TableRow) => (
              <span className="body-sm">
                {formatFinalLwd(row.ceatedAt)}
              </span>
            ),

            getSortValue: (row: OffboardL1TableRow) => row.ceatedAt ?? "",

            getTitleText: (row: OffboardL1TableRow) =>
              formatFinalLwd(row.ceatedAt),
          } satisfies ResponsiveColumn<OffboardL1TableRow>,
        ]
        : []),

      {
        id: "status",

        header: "Status",

        sortable: true,

        nowrap: true,

        render: (row) => (
          <Badge variant={offboardingStatusBadgeVariant(row.status)}>
            {formatOffboardingStatusLabel(row.status)}
          </Badge>
        ),

        getSortValue: (row) => row.status ?? "",

        getTitleText: (row) => formatOffboardingStatusLabel(row.status),
      },

      ...(isScopedClearanceApprover
        ? [
          {
            id: "finalLwd",

            header: "Last Working Day",

            sortable: true,

            nowrap: true,

            render: (row: OffboardL1TableRow) => (
              <span className="body-sm">{formatFinalLwd(row.finalLwd)}</span>
            ),

            getSortValue: (row: OffboardL1TableRow) => row.finalLwd ?? "",

            getTitleText: (row: OffboardL1TableRow) =>
              formatFinalLwd(row.finalLwd),
          } satisfies ResponsiveColumn<OffboardL1TableRow>,
        ]
        : []),

      ...(showPayrollHoldNotificationColumn
        ? [
          {
            id: "notification",

            header: "Payroll Hold Notification",

            sortable: false,

            nowrap: true,

            render: (row) => {
              const canPushNotification = row.status === "RESIGNATION_SUBMITTED";

              if (canPushNotification) {
                const isPushingNotification = pushingNotificationIds.has(
                  row.id,
                );

                return (
                  <Button
                    variant="warning"
                    size="sm"
                    className={styles.notificationButton}
                    disabled={isPushingNotification}
                    startIcon={
                      <BellRing size={15} strokeWidth={2} aria-hidden />
                    }
                    onClick={() => {
                      setNotificationOffboardingId(row.id);
                      setNotificationComment("");
                    }}
                  >
                    {isPushingNotification ? "Sending..." : "Notify"}
                  </Button>
                );
              }

              return <span className="body-sm">—</span>;
            },
          },
        ]
        : []),
      {
        id: "actions",

        header: "Action",

        sortable: false,

        nowrap: true,

        render: (row) => {
          const status = row.status;

          if (isExecutiveView && status === "RESIGNATION_SUBMITTED") {
            return (
              <div className={styles.actionGroup}>
                <Button
                  variant="accent"
                  onClick={() => openCooModal(Number(row.id))}
                >
                  Take Action
                </Button>
              </div>
            );
          }

          if (isExecutiveView && status === "UNDER_REVIEW") {
            return (
              <div className={styles.actionGroup}>
                <Button variant="link" onClick={() => navigateToDetail(row.id)}>
                  View Details
                </Button>
              </div>
            );
          }

          if (
            isHrListingView &&
            status === "HR_CLEARANCE_PENDING" &&
            !row.hrCompleted
          ) {
            const clearanceActionAvailable = canTakeClearanceAction(
              row.finalLwd,
            );

            return (
              <Button
                variant="accent"
                disabled={!clearanceActionAvailable}
                title={
                  clearanceActionAvailable
                    ? undefined
                    : CLEARANCE_ACTION_UNAVAILABLE_MESSAGE
                }
                onClick={() => navigateToDetail(row.id)}
              >
                Take Action
              </Button>
            );
          }

          if (isScopedClearanceApprover && isClearanceReviewStatus(status)) {
            const pendingDepartments = scopedClearanceDepartments.filter(
              (department) => !isDepartmentCompleted(department, row),
            );

            if (pendingDepartments.length === 0) {
              return <Badge variant="success">Completed</Badge>;
            }

            const clearanceActionAvailable = canTakeClearanceAction(
              row.finalLwd,
            );

            return (
              <Button
                variant="accent"
                disabled={!clearanceActionAvailable}
                title={
                  clearanceActionAvailable
                    ? undefined
                    : CLEARANCE_ACTION_UNAVAILABLE_MESSAGE
                }
                onClick={() => {
                  navigateToDetail(row.id);
                }}
              >
                Take Action
              </Button>
            );
          }

          if (shouldShowViewDetails(status)) {
            return (
              <Button variant="link" onClick={() => navigateToDetail(row.id)}>
                View Details
              </Button>
            );
          }

          return <span className="body-sm">—</span>;
        },
      },
    ],

    [
      isExecutiveView,
      showPayrollHoldNotificationColumn,
      isScopedClearanceApprover,
      isHrListingView,

      navigateToDetail,

      openCooModal,

      pushingNotificationIds,

      scopedClearanceDepartments,
    ],
  );

  return (
    <PageShell className={styles.pageRoot}>
      {!isScopedClearanceApprover || isHrListingView ? (
        <Card padding="3xl" className={styles.toolbarCard}>
          <div className={styles.toolbarRow}>
            {!isScopedClearanceApprover && (
              <div className={styles.toolbarField}>
                <Select
                  label="Status"
                  placeholder="All"
                  isClearable
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  options={STATUS_FILTER_OPTIONS}
                />
              </div>
            )}
            {isExecutiveView && (
              <Button
                variant="danger"
                startIcon={<UserX size={16} strokeWidth={2} aria-hidden />}
                onClick={() => setTerminationModalOpen(true)}
              >
              Process Separation
              </Button>
            )}
          </div>
        </Card>
      ) : null}

      {isScopedClearanceApprover && (
        <div className={styles.clearanceActionNotice} role="status">
          <Info
            className={styles.clearanceActionNoticeIcon}
            size={18}
            strokeWidth={2}
            aria-hidden
          />
          <p className="body-sm">{CLEARANCE_ACTION_UNAVAILABLE_MESSAGE}</p>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : (
        <div className={styles.tableSection}>
          <ResponsiveDataTable<OffboardL1TableRow>
            serialNumber
            columns={columns}
            data={paginatedRows}
            defaultSortKey="employeeId"
            defaultSortDir="asc"
            aria-label="Offboarding requests"
            emptyState={
              <EmptyStateMessage message="No offboarding requests found." />
            }
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredRows.length}
            onPageChange={setPage}
            pageSizeOptions={[10]}
          />
        </div>
      )}

      {selectedOffboardingId != null && (
        <CooActionModal
          open={cooModalOpen}
          onClose={closeCooModal}
          offboardingId={selectedOffboardingId}
          onSuccess={() => void refetch()}
        />
      )}

      <Modal
        open={notificationOffboardingId != null}
        onClose={closeNotificationModal}
        title="Payroll Hold Notification"
        size="md"
        footer={
          <div className="d-flex justify-content-end gap-1">
            <Button
              variant="light-grey"
              onClick={closeNotificationModal}
              disabled={
                notificationOffboardingId != null &&
                pushingNotificationIds.has(notificationOffboardingId)
              }
            >
              Cancel
            </Button>
            <Button
              variant="accent"
              onClick={() => {
                void handlePushPayrollNotification();
              }}
              disabled={
                notificationOffboardingId != null &&
                pushingNotificationIds.has(notificationOffboardingId)
              }
            >
              {notificationOffboardingId != null &&
                pushingNotificationIds.has(notificationOffboardingId)
                ? "Sending..."
                : "Send"}
            </Button>
          </div>
        }
      >
        <TextArea
          id="payroll-notification-comment"
          label="Comment"
          rows={4}
          maxLength={1000}
          showCharacterCount
          value={notificationComment}
          disabled={
            notificationOffboardingId != null &&
            pushingNotificationIds.has(notificationOffboardingId)
          }
          onChange={(event) => setNotificationComment(event.target.value)}
          placeholder="Add a comment for payroll..."
        />
      </Modal>

      {terminationModalOpen ? (
        <TerminationModal
          open={terminationModalOpen}
          onClose={() => setTerminationModalOpen(false)}
          onSuccess={() => void refetch()}
        />
      ) : null}
    </PageShell>
  );
}


